/* W72 STEP 5 — THE EXERCISE SHELF, AS PURE FUNCTIONS (Proposal 5, with his 2026-09-25 ~02:20 word: *"yes just
 * without these strange chips above or below each exercise row"*).
 *
 * The Library's Exercises page and the builder's library pane draw ONE component (`components/ExerciseShelf.tsx`);
 * everything that component PRINTS about a row is decided here, where a test can execute it.
 *
 * 🔴 NO CHIP IN A ROW. Kind, equipment and "also called" are CAPTION TEXT joined by ` · `; chips live only in the
 * filter row under the search. A source ruler (harness `w72-5-the-library`) forbids a chip class inside either
 * list's row.
 *
 * 🔴 ONE SEARCH, BY WHAT THE EXERCISE IS — name, muscle, equipment, and what it is "also called" (the engine's own
 * abbreviation table, GENERATED into `exerciseVocabulary.ts`). Word-AND, so `dips ring` finds `Ring Dips` as the
 * pane's placeholder promises, and the same function serves the pane and the page.
 */
import { ABBREVIATIONS, searchable } from "./exerciseVocabulary";
import { EQUIPMENT_NOT_A_PIECE } from "./equipment";
import { muscleLabel, vocabularyLabel } from "./vocabulary";
import { CATEGORY_LABEL } from "./coachExercise";
import { matchesFacets, type LibraryRow } from "./exerciseTable";

/** A Library row as the shelf reads it: the table's row plus what W72 step 5 adds. */
export type ShelfRow = LibraryRow & {
  /** How many of the coach's TEMPLATES hold it (archived templates and client copies not counted). */
  usedIn: number;
  /** `coach_exercises.hidden_at` — null means on the shelf. */
  hiddenAt: string | null;
  /** `coach_exercises.progression_config`, raw. */
  progressionConfig: unknown;
};

/* ── The one search ─────────────────────────────────────────────────────────────────────────────── */

/** Everything a query is matched against, lower-cased: the name with its long forms, the muscles (stored AND
 *  labelled), the equipment (stored AND labelled). */
export function haystack(row: {
  readonly name: string; readonly muscles?: readonly string[] | null; readonly equipment?: readonly string[] | null;
}): string {
  const parts = [searchable(row.name)];
  for (const m of row.muscles ?? []) parts.push(m.toLowerCase(), muscleLabel(m).label.toLowerCase());
  for (const e of row.equipment ?? []) parts.push(e.toLowerCase(), vocabularyLabel(e).toLowerCase());
  return parts.join(" ");
}

/** Does this row answer the query? Every word must appear somewhere; a blank query matches everything. */
export function shelfMatches(
  row: { readonly name: string; readonly muscles?: readonly string[] | null; readonly equipment?: readonly string[] | null },
  query: string,
): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter((w) => w !== "");
  if (words.length === 0) return true;
  const hay = haystack(row);
  return words.every((w) => hay.includes(w));
}

/* ── The row's words ────────────────────────────────────────────────────────────────────────────── */

/** "Also called": the name with each abbreviation spelled out — `HS Kick-Up Entry` → `Handstand Kick-Up Entry`.
 *  Null when the name carries none (a caption that repeats the name says nothing). */
export function alsoCalled(name: string): string | null {
  let changed = false;
  const out = name.replace(/[\p{L}\p{N}]+/gu, (word) => {
    const hit = ABBREVIATIONS.find((a) => a.token === word.toLowerCase());
    if (hit === undefined) return word;
    changed = true;
    return hit.expansion.replace(/(^|[\s-])(\p{L})/gu, (_m, sep: string, c: string) => sep + c.toUpperCase());
  });
  return changed ? out : null;
}

/** At most `n` labels, then `+ N more`. */
function firstFew(labels: readonly string[], n: number): string {
  if (labels.length <= n) return labels.join(", ");
  return `${labels.slice(0, n).join(", ")} + ${String(labels.length - n)} more`;
}

/** A row's stored equipment less what is not a piece (`EQUIPMENT_NOT_A_PIECE`, generated from the phone's
 *  `CanonicalEquipment.notAPiece`) — the Swift twin is `CoachLibraryFacts.pieces`. */
export function equipmentPieces(equipment: readonly string[]): string[] {
  return equipment.filter((k) => !EQUIPMENT_NOT_A_PIECE.includes(k));
}

/** The caption under a row's name: also-called · equipment · kind — TEXT, never chips. Bodyweight when no
 *  equipment is recorded; the kind only when the row is categorised. */
export function exerciseCaption(row: Pick<ShelfRow, "name" | "equipment" | "category">): string {
  const parts: string[] = [];
  const aka = alsoCalled(row.name);
  if (aka !== null) parts.push(aka);
  // `No Equipment` is not a piece (his 2026-09-28 word): a row holding only it needs nothing, and reads as Bodyweight.
  const pieces = equipmentPieces(row.equipment);
  parts.push(pieces.length === 0 ? "Bodyweight" : firstFew(pieces.map(vocabularyLabel), 2));
  if (row.category !== null) {
    const kind = CATEGORY_LABEL[row.category as keyof typeof CATEGORY_LABEL] ?? row.category;
    // `Bodyweight · Bodyweight` says one thing twice — the kind is dropped when the equipment already said it.
    if (!parts.includes(kind)) parts.push(kind);
  }
  return parts.join(" · ");
}

/** The Trains column: the primary muscles as labels (case-only duplicates folded), or "—". */
export function trainsText(muscles: readonly string[]): string {
  const labels: string[] = [];
  for (const m of muscles) {
    const l = muscleLabel(m).label;
    if (!labels.includes(l)) labels.push(l);
  }
  return labels.length === 0 ? "—" : firstFew(labels, 2);
}

/** The Level column: the coach's 1–10, "—" when unrated (never a fake middle value). */
export function levelText(difficulty: number | null): string {
  return difficulty === null ? "—" : String(difficulty);
}

/** The Used-in column. */
export function usedInText(n: number): string {
  if (n === 0) return "Not in a workout";
  return n === 1 ? "1 workout" : `${String(n)} workouts`;
}

/** How many of the coach's TEMPLATES hold each exercise — live templates only (`client_id` null, not archived),
 *  counted once per template however many times it prescribes the row. */
export function usedInByExercise(
  workouts: readonly { id: string; client_id: string | null; archived_at: string | null }[],
  sections: readonly { id: string; workout_id: string }[],
  workoutExercises: readonly { exercise_id: string; section_id: string }[],
): Map<string, number> {
  const live = new Set(workouts.filter((w) => w.client_id === null && w.archived_at === null).map((w) => w.id));
  const workoutOf = new Map(sections.map((s) => [s.id, s.workout_id]));
  const seen = new Map<string, Set<string>>();
  for (const we of workoutExercises) {
    const w = workoutOf.get(we.section_id);
    if (w === undefined || !live.has(w)) continue;
    const set = seen.get(we.exercise_id) ?? new Set<string>();
    set.add(w);
    seen.set(we.exercise_id, set);
  }
  return new Map([...seen].map(([id, s]) => [id, s.size]));
}

/* ── The filter row (chips live HERE and nowhere in a row) ──────────────────────────────────────── */

export type ShelfFilter = {
  query: string;
  /** A muscle LABEL (case-only spellings folded). */
  muscle: string | null;
  /** One of the five kinds. */
  kind: string | null;
  equipment: string | null;
  /** Used in at least one workout and carrying no rule the sentence can complete. */
  noRule: boolean;
  /** Show the hidden rows (and only them). */
  hidden: boolean;
};

export const EMPTY_SHELF_FILTER: ShelfFilter = {
  query: "", muscle: null, kind: null, equipment: null, noRule: false, hidden: false,
};

export function shelfFilterIsOn(f: ShelfFilter): boolean {
  return f.muscle !== null || f.kind !== null || f.equipment !== null || f.noRule || f.hidden;
}

/** Narrow the shelf. `hasRule` is the W73 reader (`ruleFacts(config) !== null`), passed in so this module does not
 *  decide what a rule is. Hidden rows show ONLY under the Hidden chip. */
export function filterShelf(
  rows: readonly ShelfRow[], f: ShelfFilter, hasRule: (config: unknown) => boolean,
): ShelfRow[] {
  return rows.filter((r) => {
    if (f.hidden !== (r.hiddenAt !== null)) return false;
    if (f.muscle !== null && !r.muscles.some((m) => muscleLabel(m).label === f.muscle)) return false;
    // The equipment and kind chips ask `matchesFacets` — the ONE function the builder's pane asks for the same chips,
    // so the page and the pane cannot answer "does this row match Barbell" differently.
    if (!matchesFacets(r, { equipment: f.equipment, category: f.kind })) return false;
    if (f.noRule && !(r.usedIn > 0 && !hasRule(r.progressionConfig))) return false;
    return shelfMatches(r, f.query);
  });
}

/** Why a narrowed shelf is empty — it NAMES the controls that are on (the copied page's `emptyReason` rule, carried
 *  over): telling a coach to clear a search box they never typed in sends them to the wrong control. */
export function shelfEmptyReason(f: ShelfFilter): string {
  const on: string[] = [];
  if (f.query.trim() !== "") on.push(`“${f.query.trim()}”`);
  if (f.muscle !== null) on.push("the muscle");
  if (f.kind !== null) on.push("the kind");
  if (f.equipment !== null) on.push("the equipment");
  if (f.noRule) on.push("No rule");
  if (f.hidden) on.push("Hidden");
  if (on.length === 0) return "Nothing to show.";
  if (on.length === 1) return `Nothing matches ${on[0]!}.`;
  return `Nothing matches ${on.slice(0, -1).join(", ")} and ${on[on.length - 1]!} together.`;
}

/** The foot line's count alone — `N exercises` / `N of M exercises` over the rows on the shelf. It is the WHOLE foot
 *  while the engine is withheld from coaches (`COACH_SEES_ENGINE`, his 2026-09-28 word): the rule half is engine data. */
export function shelfCountLine(shown: readonly ShelfRow[], all: readonly ShelfRow[]): string {
  const onShelf = all.filter((r) => r.hiddenAt === null);
  return shown.length === onShelf.length ? `${String(shown.length)} exercises` : `${String(shown.length)} of ${String(onShelf.length)} exercises`;
}

/** The foot line: what is shown, and how many rows a client is prescribed with no rule to progress them. */
export function shelfFoot(
  shown: readonly ShelfRow[], all: readonly ShelfRow[], hasRule: (config: unknown) => boolean,
): string {
  const onShelf = all.filter((r) => r.hiddenAt === null);
  const count = shelfCountLine(shown, all);
  const unruled = onShelf.filter((r) => r.usedIn > 0 && !hasRule(r.progressionConfig)).length;
  if (unruled === 0) return `${count} · every exercise in a workout has a rule`;
  return `${count} · ${String(unruled)} in a workout with no rule`;
}

/* ── Hide / unhide, as a model ──────────────────────────────────────────────────────────────────── */

/** The one field Hide writes and Unhide clears — `hidden_at`, stamped now or nulled. */
export function hideWrite(hide: boolean, now: Date): { hidden_at: string | null } {
  return { hidden_at: hide ? now.toISOString() : null };
}

/** The rows after a hide/unhide lands (the screen re-reads; this keeps it honest between the two). */
export function applyHide(rows: readonly ShelfRow[], id: string, write: { hidden_at: string | null }): ShelfRow[] {
  return rows.map((r) => (r.id === id ? { ...r, hiddenAt: write.hidden_at } : r));
}
